import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import styles from "./TaskCreatorSheet.module.css";
import CreatorFragment, {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import Section from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Section/Section";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import Header from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Header/Header";
import {
  useStoreId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  CreateTask,
  Task,
} from "@coworker/apprestructured/src/task-management/types/Task";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import { NotFound } from "../../NotFound/NotFound";
import { useTranslation } from "@coworker/locales";
import { useCreateTask } from "@coworker/apprestructured/src/task-management/services/useCreateTask";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { asPositiveInteger } from "@coworker/apprestructured/src/shared/helpers/string.helper";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import CreateTaskSchedule, {
  CreateTaskScheduleChangeEvent,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/CreateTaskSchedule/CreateTaskSchedule";
import { AddImages } from "@coworker/apprestructured/src/task-management/components/AddImages/AddImages";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import AttachFiles from "@coworker/apprestructured/src/task-management/components/AttachFiles/AttachFiles";
import { useFileUploader } from "@coworker/apprestructured/src/uploader/useFileUploader";
import { Recurrence } from "@coworker/apprestructured/src/task-management/types/Schedule";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { useCreateSchedule } from "@coworker/apprestructured/src/task-management/services/useCreateSchedule";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";

/**
 * The TaskCreatorSheet is open for the task type with id 'taskCreator' taken
 * from the url search params. Open/close the sheet by adding/removing a
 * 'taskCreator' search param, or use the 'useSearchParamState' hook to reduce
 * some boilerplate.
 */
function TaskCreatorSheet() {
  const [{ taskCreator }, setSearchParams] = useSearchParamState();
  const taskTypeId = asPositiveInteger(taskCreator);
  const [taskType, setTaskType] = useState<TaskType | null>(null);
  const [task, setTask] = useState<Record<string, unknown> | null>(null);
  const [taskTypeNotFound, setTaskTypeNotFound] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [images, setImages] = useState<NewOrExistingFile[]>([]);
  const [files, setFiles] = useState<NewOrExistingFile[]>([]);
  const [scheduleStart, setScheduleStart] = useState<string>("");
  const [scheduleEnd, setScheduleEnd] = useState<string>("");
  const [scheduleRecurrence, setScheduleRecurrence] =
    useState<Recurrence | null>(null);

  const myUserId = useUID();
  const myStoreId = useStoreId();

  const { t } = useTranslation();

  const { mutateAsync: createTaskMutation, isPending: isCreatingTask } =
    useCreateTask();
  const { mutateAsync: createScheduleMutation, isPending: isCreatingSchedule } =
    useCreateSchedule();
  const { data: taskTypes } = useAllTaskTypes();

  const handleTaskTypeIdChange = useEffectEvent(
    (taskTypeId: number, taskTypes: TaskType[]) => {
      const taskType = taskTypes.find(({ id }) => id === taskTypeId);
      setTaskTypeNotFound(!taskType);
      if (taskType) {
        setTaskType(taskType);

        const details = taskType.details.reduce(
          (res, curr) => {
            res[curr.name] = curr.initial;
            return res;
          },
          {} as Record<string, unknown>
        );

        setTask({
          title: "",
          priority: false,
          assigned_user_id: null,
          assigned_team_id: null,
          task_type_id: taskType.id,
          state: "pending",
          close_reason_id: null,
          close_description: "",
          image_ids: [],
          file_ids: [],
          details,
          creator_user_id: myUserId,
          store_id: myStoreId,
          start: null,
        });
        setShowValidation(false);
        setImages([]);
        setFiles([]);
        setScheduleStart("");
        setScheduleEnd("");
        setScheduleRecurrence(null);
      }
    }
  );

  useEffect(() => {
    if (taskTypeId && taskTypes) {
      handleTaskTypeIdChange(taskTypeId, taskTypes);
    }
  }, [taskTypeId, taskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const creatorFragmentRefs = useRef<Map<unknown, CreatorFragmentHandle>>(
    new Map()
  );

  const setCreatorFragmentRef = useCallback(
    (key: unknown) => (element: CreatorFragmentHandle | null) => {
      if (element) {
        creatorFragmentRefs.current.set(key, element);
      } else {
        creatorFragmentRefs.current.delete(key);
      }
    },
    []
  );

  const {
    ready: imageUploaderIsReady,
    upload: uploadImages,
    createUploads: imagesCreateUploads,
  } = useImageUploader(images);

  const {
    ready: fileUploaderIsReady,
    upload: uploadFiles,
    createUploads: filesCreateUploads,
  } = useFileUploader(files);

  const queryClient = useQueryClient();

  const handleMutationResults = useCallback(
    (
      createdTaskResult: PromiseSettledResult<Task>,
      imageUploadResult: PromiseSettledResult<void>,
      fileUploadResult: PromiseSettledResult<void>
    ) => {
      handleImageUploadResult(imageUploadResult, createdTaskResult);
      handleFileUploadResult(fileUploadResult, createdTaskResult);
      if (createdTaskResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskCreator");
          prev.set("taskViewer", String(createdTaskResult.value["id"]));
        });
      }
    },
    [disableSaveGuard, queryClient, setSearchParams]
  );

  const createTask = useCallback(() => {
    async function fn() {
      const [createdTaskResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          createTaskMutation({
            ...task,
            images: imagesCreateUploads,
            files: filesCreateUploads,
          } as CreateTask),
          uploadImages(),
          uploadFiles(),
        ]);
      handleMutationResults(
        createdTaskResult,
        imageUploadResult,
        fileUploadResult
      );
    }
    fn();
  }, [
    createTaskMutation,
    filesCreateUploads,
    handleMutationResults,
    imagesCreateUploads,
    task,
    uploadFiles,
    uploadImages,
  ]);

  const createSchedule = useCallback(() => {
    async function fn() {
      const {
        title,
        priority,
        details,
        task_type_id,
        creator_user_id,
        assigned_user_id,
        assigned_team_id,
        store_id,
      } = task as CreateTask;
      const [createdTaskResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          createScheduleMutation({
            start: scheduleStart,
            end: scheduleEnd,
            recurrence: scheduleRecurrence!,
            task: {
              title,
              priority,
              details,
              task_type_id,
              creator_user_id,
              assigned_user_id,
              assigned_team_id,
              store_id,
              images: imagesCreateUploads,
              files: filesCreateUploads,
            },
          }),
          uploadImages(),
          uploadFiles(),
        ]);
      handleMutationResults(
        createdTaskResult,
        imageUploadResult,
        fileUploadResult
      );
    }
    fn();
  }, [
    createScheduleMutation,
    filesCreateUploads,
    handleMutationResults,
    imagesCreateUploads,
    scheduleEnd,
    scheduleRecurrence,
    scheduleStart,
    task,
    uploadFiles,
    uploadImages,
  ]);

  const create = useCallback(() => {
    if (scheduleRecurrence == null) {
      createTask();
    } else {
      createSchedule();
    }
  }, [createSchedule, createTask, scheduleRecurrence]);

  const handleCreateClick = useCallback(() => {
    const someFragmentsAreInvalid = creatorFragmentRefs.current
      .values()
      .some((creatorFragmentHandle) => {
        return !creatorFragmentHandle.valid;
      });

    if (someFragmentsAreInvalid) {
      setShowValidation(someFragmentsAreInvalid);
    } else {
      create();
    }
  }, [create]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskCreator");
    });
  }, [setSearchParams]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      enableSaveGuard();
      setTask((prev) => {
        if (!prev) {
          return prev;
        }
        return merge(prev, part);
      });
    },
    [enableSaveGuard]
  );

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      enableSaveGuard();
      setImages(newImages);
    },
    [enableSaveGuard]
  );

  const blockInput =
    isCreatingTask || isCreatingSchedule || !taskTypeId || !taskType;

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput,
      task: task ? task : LOADING,
      onTaskChange: handleTaskChange,
      showValidation,
    };
  }, [blockInput, handleTaskChange, showValidation, task]);

  const header = useMemo(() => {
    if (taskTypeNotFound) {
      return <FixaModalHeader closeBtnClick={handleCloseClick} />;
    } else {
      return (
        <FixaModalHeader
          closeBtnClick={handleCloseClick}
          title={t(taskType?.label || "createString")}
        />
      );
    }
  }, [handleCloseClick, taskTypeNotFound, taskType, t]);

  const footer = useMemo(() => {
    if (taskTypeNotFound) {
      return null;
    } else {
      return (
        <FixaModalFooter>
          <FixaButton
            onClick={handleCreateClick}
            text={`${t("create4String")} ${t(taskType?.label || "")}`}
            fluid
            type="primary"
            loading={isCreatingTask || isCreatingSchedule}
            disabled={
              !taskTypeId ||
              !taskType ||
              !imageUploaderIsReady ||
              !fileUploaderIsReady
            }
            htmlType="button"
          />
        </FixaModalFooter>
      );
    }
  }, [
    taskTypeNotFound,
    handleCreateClick,
    t,
    isCreatingTask,
    isCreatingSchedule,
    taskTypeId,
    taskType,
    imageUploaderIsReady,
    fileUploaderIsReady,
  ]);

  const handleScheduleChange = useCallback(
    ({
      futureStart,
      repeatingStart,
      end,
      recurrence,
    }: CreateTaskScheduleChangeEvent) => {
      handleTaskChange({ start: futureStart });
      setScheduleStart(repeatingStart);
      setScheduleEnd(end);
      setScheduleRecurrence(recurrence);
    },
    [handleTaskChange]
  );

  const content = useMemo(() => {
    if (taskTypeNotFound) {
      return (
        <NotFound
          title={t("sorryTaskCantBeCreatedString")}
          message={t("thisTaskTypeDoesNotExistString")}
        />
      );
    } else {
      return (
        <div className={styles["sheetsContent"]}>
          <Section>
            <Header number="1" label={t("taskDetailsString")} />
            <FixaListView id="details">
              {taskType ? (
                taskType.creator_fragments.map((config, i) => {
                  return (
                    <CreatorFragment
                      key={i}
                      ref={setCreatorFragmentRef(i)}
                      config={config}
                      commonProps={creatorFragmentCommonProps}
                    />
                  );
                })
              ) : (
                <LoadingCreatorFragment />
              )}
            </FixaListView>
          </Section>
          <Section>
            <Header number="2" label={t("taskSettingsString")} />
            <FixaListView id="settings">
              <CreatorFragment
                config={{ id: "assign-to-creator-fragment", props: {} }}
                commonProps={creatorFragmentCommonProps}
                ref={setCreatorFragmentRef("assign-to")}
              />
              <CreatorFragment
                config={{
                  id: "switch-creator-fragment",
                  props: {
                    field: "priority",
                    label: t("priorityString"),
                    icon: "notice-circle",
                  },
                }}
                commonProps={creatorFragmentCommonProps}
                ref={setCreatorFragmentRef("priority")}
              />
              <CreateTaskSchedule
                disabled={blockInput}
                futureStart={(task?.["start"] || "") as string}
                onChange={handleScheduleChange}
                repeatingStart={scheduleStart}
                end={scheduleEnd}
                recurrence={scheduleRecurrence}
              />
              <AddImages
                images={images}
                onConfirmClick={handleImagesChange}
                disabled={blockInput}
              />
              <AttachFiles
                disabled={blockInput}
                files={files}
                onFilesChanged={setFiles}
              />
            </FixaListView>
          </Section>
        </div>
      );
    }
  }, [
    taskTypeNotFound,
    t,
    taskType,
    creatorFragmentCommonProps,
    setCreatorFragmentRef,
    blockInput,
    task,
    handleScheduleChange,
    scheduleStart,
    scheduleEnd,
    scheduleRecurrence,
    images,
    files,
    handleImagesChange,
  ]);

  return (
    <FixaModal visible={!!taskTypeId} handleCloseBtn={handleCloseClick}>
      <FixaSheets
        fullSize
        className={taskTypeNotFound ? "" : styles["greyBackground"]}
        header={header}
        footer={footer}
      >
        {content}
      </FixaSheets>
    </FixaModal>
  );
}

function handleImageUploadResult(
  imageUploadResult: PromiseSettledResult<void>,
  createdTaskResult: PromiseSettledResult<Task>
) {
  if (imageUploadResult.status === "rejected") {
    const imageUploadIds =
      createdTaskResult.status === "fulfilled"
        ? createdTaskResult.value.image_ids
        : "unknown";
    reportMessageToSentry(
      "Image upload failed",
      { uploadIds: imageUploadIds },
      "error"
    );
  }
}

function handleFileUploadResult(
  fileUploadResult: PromiseSettledResult<void>,
  createdTaskResult: PromiseSettledResult<Task>
) {
  if (fileUploadResult.status === "rejected") {
    const fileUploadIds =
      createdTaskResult.status === "fulfilled"
        ? createdTaskResult.value.file_ids
        : "unknown";
    reportMessageToSentry(
      "File upload failed",
      { uploadIds: fileUploadIds },
      "error"
    );
  }
}

export default TaskCreatorSheet;
